import React, { useMemo } from "react";
import "./SeasonalOverlay.css";

// Site-wide seasonal decoration overlay. Reads `active_season` from the
// site theme (piped through App's useTheme hook) and renders a
// pointer-events-none absolute-positioned canvas of drifting sprites
// covering the entire viewport. Each season is a pure-CSS animation
// with a small React sprite generator — no images, no dependencies,
// no runtime perf hit beyond the ~30-60 animated elements each preset
// spawns.
//
// Seasons:
//   • none              → renders null (default year-round)
//   • christmas         → drifting snowflakes (❄ ❆ ✻ glyphs)
//   • halloween         → drifting bats + occasional pumpkins
//   • easter            → bouncing pastel eggs
//   • valentines        → floating red/pink hearts
//   • st-patrick        → drifting clovers / four-leaves
//   • fourth-of-july    → periodic fireworks bursts (red / white / blue)
//
// Design intent: the overlay must NOT block any viewer interaction —
// `pointer-events: none` at every level, `z-index` above CRT visuals
// but below headers / modals so it feels environmental rather than
// intrusive. The CRT tube's own vignette + phosphor stays fully
// visible; the sprites drift over the cabinet + room area too.

// Per-season sprite configuration, with optional per-room overrides
// so each (season, room) combo gets its own distinctive mood rather
// than the same 60 snowflakes drifting through every environment.
//
// Structure: each season has a `default` sprite bag + an optional
// `byRoom` map keyed by the room preset. If the current room has no
// override entry the default is used — safe fallback.
//
// Mood matrix rationale:
//   • Halloween + cabin  → warm amber "firefly" pinpoints (a fire-lit
//     cabin at dusk with fireflies feels seasonal without dropping
//     literal bat emoji in front of the tube).
//   • Halloween + arcade → green pixel skulls (8-bit spooky).
//   • Halloween + void   → ghost wisps (dot glyphs on deep purple).
//   • Christmas + cabin  → softer, less-dense snowfall (cabin already
//     feels wintery).
//   • Christmas + arcade → cyan pixel stars (retro CRT holiday).
//   • Christmas + void   → cold blue points of light.
//   • Christmas + vhs-store → magenta tinsel (nostalgic '80s tinsel).
//   • Valentines + cabin → warm ember dots (candlelight mood).
//   • Valentines + arcade → pink pixel hearts.
//   • Valentines + void  → deep rose petals.
//   • Easter + cabin     → wildflowers instead of emoji rabbits.
//   • St-patrick + cabin → green fireflies + clovers.
//   • Fourth-of-july     → fireworks (no room override; firework
//     bursts already read as environmental regardless of the room).
const SEASON_GLYPHS = {
    christmas: {
        default: { chars: ["❄", "❅", "❆", "✻", "❋"], color: "#E8F4FF", count: 60 },
        byRoom: {
            cabin:      { chars: ["❄", "❅"],            color: "#DCECFF", count: 35 },
            arcade:     { chars: ["✦", "✧"],            color: "#00FFFF", count: 45 },
            void:       { chars: ["✦", "★"],            color: "#66AAFF", count: 55 },
            "vhs-store":{ chars: ["✦", "❆", "✧"],       color: "#FF66FF", count: 40 },
        },
    },
    halloween: {
        default: { chars: ["🦇", "🎃", "👻"], color: "#FF8C00", count: 35 },
        byRoom: {
            cabin:  { chars: ["·", "✦", "✧"], color: "#FFB347", count: 55 }, // fireflies
            arcade: { chars: ["💀", "👾"],     color: "#33FF33", count: 30 }, // pixel skulls
            void:   { chars: ["·", "◦", "○"], color: "#B39DFF", count: 40 }, // wisps
        },
    },
    easter: {
        default: { chars: ["🥚", "🐰", "🌷"], color: "#FFB6C1", count: 30 },
        byRoom: {
            cabin:  { chars: ["🌷", "🌸", "🌼"], color: "#FFD1DC", count: 28 },
            arcade: { chars: ["🥚"],             color: "#FFAAFF", count: 26 },
        },
    },
    valentines: {
        default: { chars: ["♥", "♡", "❤"], color: "#FF3366", count: 40 },
        byRoom: {
            cabin:  { chars: ["·", "✦", "♥"], color: "#FFAA77", count: 45 }, // candlelight embers
            arcade: { chars: ["♥"],           color: "#FF00AA", count: 42 }, // pixel hearts
            void:   { chars: ["♥", "·"],      color: "#FF66AA", count: 35 }, // deep rose
        },
    },
    "st-patrick": {
        default: { chars: ["☘", "🍀", "🌱"], color: "#00A651", count: 45 },
        byRoom: {
            cabin:  { chars: ["☘", "·", "✦"], color: "#66FF66", count: 45 },
            arcade: { chars: ["☘"],           color: "#00FF00", count: 40 },
        },
    },
    "fourth-of-july": { chars: ["✦", "✧", "★", "✵"], color: "#FFFFFF", count: 50 },
};

// Resolve the correct sprite bag for the current (season, room) pair.
// - Returns null if the season key is unknown (caller shows nothing).
// - Falls back to the season's `default` if the room has no override.
// - Accepts the legacy "flat" shape (fourth-of-july above) for
//   backwards compatibility with any season we haven't nested yet.
function resolveSpriteConfig(season, room) {
    const entry = SEASON_GLYPHS[season];
    if (!entry) return null;
    // Legacy flat shape (single global bag, no room variants).
    if (Array.isArray(entry.chars)) return entry;
    if (room && entry.byRoom && entry.byRoom[room]) return entry.byRoom[room];
    return entry.default;
}

// Deterministic per-sprite random position/delay/duration seeded off
// the index so re-renders don't cause the field to visually reset on
// every parent state change. Real random each MOUNT via `useMemo` on
// the season+count key.
function makeSprites(season, room) {
    const cfg = resolveSpriteConfig(season, room);
    if (!cfg || !cfg.count) return [];
    const sprites = [];
    for (let i = 0; i < cfg.count; i++) {
        const glyph = cfg.chars[i % cfg.chars.length];
        // Random horizontal starting position 0-100% and a per-sprite
        // duration 12-28s so the field doesn't feel mechanical.
        const left = Math.random() * 100;
        const duration = 12 + Math.random() * 16;
        const delay = -Math.random() * duration; // negative so first paint isn't all lined up at the top
        const scale = 0.6 + Math.random() * 1.2;
        const drift = -8 + Math.random() * 16; // horizontal drift px
        const opacity = 0.35 + Math.random() * 0.55;
        sprites.push({ i, glyph, left, duration, delay, scale, drift, opacity });
    }
    return sprites;
}

// Fourth-of-July fireworks are a different beast — instead of
// falling sprites we render short-lived burst bloom points at random
// positions on a slow interval. Each burst is a nested layer of
// radial "sparks" (styled ✦ characters) animated outward from a
// center point.
function makeFireworkBursts(count = 6) {
    const bursts = [];
    for (let i = 0; i < count; i++) {
        const top = 10 + Math.random() * 60;   // % — upper 70% of viewport
        const left = 10 + Math.random() * 80;  // %
        const delay = i * 1.5 + Math.random() * 0.8; // stagger seconds
        // Cycle of red / white / blue coloured bursts
        const colors = ["#E63946", "#FFFFFF", "#457B9D"];
        const color = colors[i % colors.length];
        bursts.push({ i, top, left, delay, color });
    }
    return bursts;
}

export function SeasonalOverlay({ season, room }) {
    // `useMemo` on (season, room) — recompute sprite positions when
    // the owner switches season OR room but not on every parent
    // re-render. The room key means switching Owner Bar rooms mid-
    // season swaps the sprite palette without a page reload.
    const sprites = useMemo(() => makeSprites(season, room), [season, room]);
    const bursts = useMemo(() => (season === "fourth-of-july" ? makeFireworkBursts(6) : []), [season]);

    if (!season || season === "none") return null;

    // Fourth-of-July renders bursts instead of drifting sprites.
    if (season === "fourth-of-july") {
        return (
            <div
                className="seasonal-overlay pointer-events-none"
                data-testid={`seasonal-overlay-${season}`}
                aria-hidden="true"
            >
                {bursts.map((b) => (
                    <div
                        key={b.i}
                        className="firework-burst"
                        style={{
                            top: `${b.top}%`,
                            left: `${b.left}%`,
                            animationDelay: `${b.delay}s`,
                            color: b.color,
                        }}
                    >
                        {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
                            <span
                                key={angle}
                                className="firework-spark"
                                style={{ transform: `rotate(${angle}deg)` }}
                            >
                                ✦
                            </span>
                        ))}
                    </div>
                ))}
            </div>
        );
    }

    // Resolve the sprite palette one more time for the sprite render
    // (color live-updates when room changes mid-season).
    const cfg = resolveSpriteConfig(season, room);
    return (
        <div
            className={`seasonal-overlay seasonal-overlay-${season} pointer-events-none`}
            data-testid={`seasonal-overlay-${season}`}
            data-room={room || ""}
            aria-hidden="true"
        >
            {sprites.map((s) => (
                <span
                    key={s.i}
                    className="seasonal-sprite"
                    style={{
                        left: `${s.left}%`,
                        animationDuration: `${s.duration}s`,
                        animationDelay: `${s.delay}s`,
                        transform: `scale(${s.scale})`,
                        opacity: s.opacity,
                        // Custom prop for horizontal drift used in the
                        // CSS animation keyframes via var().
                        "--drift": `${s.drift}px`,
                        color: cfg ? cfg.color : "#FFFFFF",
                    }}
                >
                    {s.glyph}
                </span>
            ))}
        </div>
    );
}
